import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';

/**
 * Safely format a date with fallback handling
 * @param {string|Date} dateValue - The date to format
 * @param {string} formatString - The format string for date-fns
 * @param {string} fallback - Fallback text if date is invalid
 * @returns {string} Formatted date or fallback
 */
export const safeFormatDate = (dateValue, formatString = 'MMM dd, yyyy', fallback = 'Date not available') => {
  if (!dateValue) {
    return fallback;
  }

  try {
    let date;
    
    // Handle different input types
    if (typeof dateValue === 'string') {
      // Try parsing as ISO string first
      date = parseISO(dateValue);
      
      // If that fails, try regular Date constructor
      if (!isValid(date)) {
        date = new Date(dateValue);
      }
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return fallback;
    }

    // Check if date is valid
    if (!isValid(date) || isNaN(date.getTime())) {
      // If original value looks like a formatted date, return it as-is
      if (typeof dateValue === 'string' && dateValue.length > 0) {
        return dateValue;
      }
      return fallback;
    }

    return format(date, formatString);
  } catch (error) {
    console.warn('Date formatting error:', error, 'for value:', dateValue);
    
    // If original value looks like a formatted date, return it as-is
    if (typeof dateValue === 'string' && dateValue.length > 0) {
      return dateValue;
    }
    return fallback;
  }
};

/**
 * Safely format relative time (e.g., "2 hours ago")
 * @param {string|Date} dateValue - The date to format
 * @param {object} options - Options for formatDistanceToNow
 * @param {string} fallback - Fallback text if date is invalid
 * @returns {string} Formatted relative time or fallback
 */
export const safeFormatRelativeTime = (dateValue, options = { addSuffix: true }, fallback = 'Unknown time') => {
  if (!dateValue) {
    return fallback;
  }

  try {
    let date;
    
    if (typeof dateValue === 'string') {
      date = parseISO(dateValue);
      if (!isValid(date)) {
        date = new Date(dateValue);
      }
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return fallback;
    }

    if (!isValid(date) || isNaN(date.getTime())) {
      return fallback;
    }

    return formatDistanceToNow(date, options);
  } catch (error) {
    console.warn('Relative time formatting error:', error, 'for value:', dateValue);
    return fallback;
  }
};

/**
 * Safely combine date and time strings
 * @param {string} dateStr - Date string
 * @param {string} timeStr - Time string
 * @param {string} formatString - Format for output
 * @param {string} fallback - Fallback text
 * @returns {string} Formatted date-time or fallback
 */
export const safeFormatDateTime = (dateStr, timeStr, formatString = 'MMM dd, yyyy h:mm a', fallback = 'Date not available') => {
  if (!dateStr && !timeStr) {
    return fallback;
  }

  try {
    // If we have both date and time
    if (dateStr && timeStr) {
      const dateTimeStr = `${dateStr} ${timeStr}`;
      const date = new Date(dateTimeStr);
      
      if (isValid(date)) {
        return format(date, formatString);
      }
    }

    // If we only have date
    if (dateStr) {
      return safeFormatDate(dateStr, 'MMM dd, yyyy', dateStr);
    }

    // If we only have time
    if (timeStr) {
      return timeStr;
    }

    return fallback;
  } catch (error) {
    console.warn('DateTime formatting error:', error);
    return `${dateStr || ''} ${timeStr || ''}`.trim() || fallback;
  }
};

/**
 * Check if a date value is valid
 * @param {string|Date} dateValue - The date to check
 * @returns {boolean} Whether the date is valid
 */
export const isValidDate = (dateValue) => {
  if (!dateValue) return false;
  
  try {
    let date;
    
    if (typeof dateValue === 'string') {
      date = parseISO(dateValue);
      if (!isValid(date)) {
        date = new Date(dateValue);
      }
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return false;
    }

    return isValid(date) && !isNaN(date.getTime());
  } catch (error) {
    return false;
  }
};

/**
 * Common date format patterns
 */
export const DATE_FORMATS = {
  FULL: 'EEEE, MMMM d, yyyy',
  LONG: 'MMMM d, yyyy',
  MEDIUM: 'MMM d, yyyy',
  SHORT: 'MM/dd/yyyy',
  TIME: 'h:mm a',
  DATETIME: 'MMM d, yyyy h:mm a',
  DATETIME_FULL: 'EEEE, MMMM d, yyyy h:mm a',
  ISO_DATE: 'yyyy-MM-dd',
  ISO_DATETIME: 'yyyy-MM-dd HH:mm:ss'
};

export default {
  safeFormatDate,
  safeFormatRelativeTime,
  safeFormatDateTime,
  isValidDate,
  DATE_FORMATS
};
